import type * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as excel from '@attain-apps/fe/web/main/lib/excel'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type * as me from '../../../me'
import type { Entity, TypeParams } from '../types'

import type { ProposalInfo } from './proposal-info'
import { getWorkbook } from './workbook-generator'

/**
 * Used as the last part of the file name when downloading all of the reports
 */
const RATE_FULL_PROPOSAL_DOWNLOAD_NAME = 'Full Proposal'

export class Factory extends excel.ec.Factory<TypeParams> {
  constructor({ meFeature }: Factory.constructor.Params) {
    super(reportsEc => ({
      download: ({ entities }) => {
        const tenantName = meFeature.contexts.selectors.tenant.asSignal()?.name
        // TODO: get actual proposal title from scenario variables
        const proposalTitle =
          meFeature.contexts.selectors.scenario.asSignal()?.name
        const proposalInfo: ProposalInfo = {
          proposalTitle,
          tenantName,
        }

        return this._loadReportsWithContent(entities, reportsEc).pipe(
          rxjs.map(reports => ({
            filename: this._getFilename(reports, proposalInfo),
            workbook: getWorkbook(reports, proposalInfo),
          })),
        )
      },
    }))
  }

  /**
   * TODO:
   */
  private _getFilename(
    reports: readonly Entity[],
    { proposalTitle, tenantName }: ProposalInfo,
  ): string {
    /** Indicates what proposal this file is for */
    const fullProposalName =
      tenantName && proposalTitle
        ? `${tenantName} (${proposalTitle})`
        : (tenantName ?? proposalTitle)

    /** Indicates what content is included in this file */
    const contentName =
      reports.length === 1
        ? `Report ${reports[0].label.code}`
        : RATE_FULL_PROPOSAL_DOWNLOAD_NAME

    /**
     * We want the file name to convey which proposal this is for and what
     * content is being downloaded
     */
    return [fullProposalName, contentName]
      .filter(TypeChecker.isNotNullish.raw)
      .join(' - ')
  }

  /**
   * With reports, we typically load the list without content, but then retrieve
   * content for individual reports when navigating to their page. To download
   * the Excel file, we need all the reports with their content. So this
   * retrieves the content for any report that does not already have it
   */
  private _loadReportsWithContent(
    originalReports: readonly Entity[],
    reportsEc: ec.Collection<TypeParams>,
  ): rxjs.Observable<readonly Entity[]> {
    /**
     * Identifies the reports that do not currently have content
     */
    const reportIdsMissingContent = originalReports
      .filter(report => !report.content)
      .map(report => report.label.id)
    /**
     * Retrieves content for the reports that did not have it
     */
    const retrievedReports$ = reportIdsMissingContent.length
      ? reportsEc.plugins.dataOperationPlugin.feature.actions.list
          .from('Excel download')
          .execute({ ids: reportIdsMissingContent }).asObservable
      : rxjs.of({ entities: [] }) // return an empty list if no reports need content

    // After retrieving the necessary reports...
    return retrievedReports$.pipe(
      rxjs.map(({ entities: retrievedReports }) => {
        // We store all the retrieved reports in a lookup...
        const retrievedReportLookup: Partial<Record<string, Entity>> = {}
        for (const report of retrievedReports) {
          retrievedReportLookup[report.label.id] = report
        }
        // And then we return the original list of reports, but...
        return originalReports.map(
          // Wherever the report matches a retrieved report, we replace it with
          // the retrieved report
          report => retrievedReportLookup[report.label.id] ?? report,
        )
      }),
      rxjs.catchError(error => {
        console.warn('Failed to load all reports for download!', error)
        // Attempts to produce a download with whatever entities we do have
        return reportsEc.plugins.cachePlugin.feature.selectors.lookup.asObservable.pipe(
          // Uses latest value of entity cache
          rxjs.take(1),
          rxjs.map(cachedReports =>
            originalReports.map(
              // Wherever the report matches a cached report, we replace it with
              // the cached report
              report => cachedReports[report.label.id] ?? report,
            ),
          ),
        )
      }),
    )
  }
}
export namespace Factory {
  export namespace constructor {
    export interface Params {
      meFeature: me.Feature
    }
  }
}
